import React, { useEffect, useState } from 'react';

import { useAppContext } from 'src/components/app-context';
import { useOfferListFilterContext } from 'src/areas/product/components/offer-list-filter-context';
import type { ExtendedPrice } from '../../product-context';
import { useProductContext } from '../../product-context';

import {
  CaptionText,
  H3Text,
  FontScale,
  FontWeight,
} from '@prisjakt/ui/text/variants';
import PriceInformation from '../../price-information';
import { sortOffersByPrice } from 'src/components/price-list/sort-by-price';
import PriceListHeader from './price-list-header';
import { PriceListBody } from './price-list-body';
import { sortByFastestDelivery } from 'src/components/price-list/sort-by-fastest-delivery';
import MissingShop from '../../missing-shop';
import OfferListEmptyMessage from '../offer-list-empty-message';
import type { Option } from 'src/components/filter-dropdown';
import { OptionTypeEnum } from 'src/components/filter-dropdown';

import {
  getSplitOfferlist,
  getListLimits,
} from '../../../utils/price-list/split-list';
import { getOurChoiceOffer } from '../../product-header/components/our-choice/utils';

import { trackPriceListOfferSortingChange } from '../../tracking';

import type { IListPrices, IPriceListPanelProps } from './types';

import { PricePanel, HeaderWrapper } from './styles';

export const PriceListPanel = (props: IPriceListPanelProps) => {
  const { me, showProductInformationTexts, storeStatistics, campaign } = props;
  const { t, market } = useAppContext();
  const { getActiveFilters, getPriceDisplay } = useOfferListFilterContext();
  const { product, offers, allOffers, allOffersWithActiveAdditives } =
    useProductContext();

  const [currentSorting, setCurrentSorting] = useState<OptionTypeEnum>(
    OptionTypeEnum.CHEAPEST_PRICE
  );
  const [sortedOffers, setSortedOffers] = useState<IListPrices>({
    mainList: offers.filteredOffers,
    secondaryList: [],
  });
  const [ourChoice, setOurChoice] = useState<ExtendedPrice | undefined>(
    getOurChoiceOffer(offers.filteredOffers, market.code)
  );

  useEffect(() => {
    getOffersList();
  }, [currentSorting, offers]);

  const getOffersList = () => {
    const offersSorted = sortOffersByPrice(
      getPriceDisplay(),
      offers.filteredOffers
    );
    setOurChoice(getOurChoiceOffer(offersSorted, market.code));
    const splitOfferList = getSplitOfferlist(offersSorted, getActiveFilters());
    const newSortedOffers = sortByFastestDelivery(
      splitOfferList,
      currentSorting,
      getActiveFilters()
    );

    setSortedOffers(newSortedOffers);
  };

  const handleSort = (option: Option) => {
    if (!option.type || option.type === currentSorting) {
      return;
    }
    setCurrentSorting(option.type);
    trackPriceListOfferSortingChange(product.id, option.type);
  };

  const listLimits = getListLimits(sortedOffers);
  const secondaryListHeaderText = `${t('product', 'splitlist-other.title')}
    (${sortedOffers.secondaryList.length})`;

  return (
    <PricePanel>
      {allOffers.length > 0 && (
        <PriceListHeader
          onSort={handleSort}
          sorting={currentSorting}
          allOffers={allOffers}
          allOffersWithActiveAdditives={allOffersWithActiveAdditives}
        />
      )}
      {me?.analyticsAccess?.stores && <MissingShop />}

      <OfferListEmptyMessage offers={sortedOffers} allOffers={allOffers} />

      {sortedOffers.mainList.length > 0 && (
        <PriceListBody
          isPrimaryList
          sortedPrices={sortedOffers.mainList}
          numberOfVisiblePrices={listLimits.mainListLimit}
          currentSorting={currentSorting}
          showProductInformationTexts={Boolean(showProductInformationTexts)}
          campaign={campaign}
          ourChoiceStoreId={ourChoice?.store?.id}
        />
      )}
      {sortedOffers.secondaryList.length > 0 && (
        <>
          <HeaderWrapper>
            <H3Text>{secondaryListHeaderText}</H3Text>
            <CaptionText scale={FontScale.S} weight={FontWeight.HEAVIER}>
              {t('product', 'splitlist-other.description')}
            </CaptionText>
          </HeaderWrapper>
          <PriceListBody
            isPrimaryList={false}
            sortedPrices={sortedOffers.secondaryList!}
            numberOfVisiblePrices={listLimits.secondaryListLimit}
            currentSorting={currentSorting}
            showProductInformationTexts={Boolean(showProductInformationTexts)}
            campaign={campaign}
            ourChoiceStoreId={ourChoice?.store?.id}
          />
        </>
      )}

      {storeStatistics && Boolean(storeStatistics.totalCount) && (
        <PriceInformation storeStatistics={storeStatistics} />
      )}
    </PricePanel>
  );
};
